/**
 * "Why does this game say I have no money" — the in-play half of the promotion
 * play restriction (#546).
 *
 * A provider session opened BEFORE the bonus was claimed never passes the launch
 * gate again, so every bet placed in it is refused at the seamless debit instead
 * (`apps/game/src/providers/engine/dispatcher.ts`). The seamless protocol has no
 * "not allowed by your bonus" code, so that refusal leaves us as
 * INSUFFICIENT_FUNDS and the camp's own UI tells the player their balance is too
 * low — while their balance is untouched and they take the wrong problem to
 * support. The truth cannot travel on the wire, so it is told here, on our own
 * chrome, over the frame that is saying the wrong thing.
 */
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/icon.js';
import { usePlayRestrictions, type PlayRestrictions } from './play-restrictions.js';
import { isOriginalsCard, useGamePromoLock, type LockableCard } from './use-game-lock.js';

/**
 * Nothing pushes the flip into an open provider frame: the claim that creates
 * the restriction can be made in another tab, or granted by an admin, and the
 * player never leaves this page — so the shared query's `refetchOnWindowFocus`
 * never fires either. Polling the query the lobby already owns is the mechanism
 * the app has; it costs one small request per interval, for as long as the one
 * frame a player can have open stays open.
 */
const RESTRICTION_POLL_MS = 30_000;

/**
 * Is the refusal this notice would explain CERTAIN, or only the lobby's
 * conservative guess?
 *
 * A tile is locked when its game type is unknown to a type-restricted
 * promotion: the lobby refuses to open a card it cannot judge, which is what
 * svc-game answers for it too. An EXPLANATION must not be given on that same
 * guess — a launch surface carrying no catalog type (recently-played sends id +
 * provider only) would otherwise tell a player their game is excluded when the
 * server, which resolves the type from the catalog, lets it through.
 *
 * A camp outside the allow-list is the other case entirely. That refusal does
 * not depend on the type at all: the gate reads the provider first and every
 * bet from this camp is refused whatever the catalog says the game is. So a
 * bonus restricting BOTH dimensions must still be explained on a forbidden
 * camp, which is the common shape — a promotion naming its camps usually names
 * its game types too.
 */
function refusalIsCertain(game: LockableCard, restrictions: PlayRestrictions): boolean {
  const { allowedProviders, allowedGameTypes } = restrictions;
  if (allowedProviders !== null && game.providerId && !allowedProviders.includes(game.providerId)) {
    return true;
  }
  if (allowedGameTypes === null) return true;
  return Boolean(game.gameType) || isOriginalsCard(game);
}

export function GameRestrictionNotice({ game }: { game: LockableCard | null | undefined }) {
  const { t } = useTranslation('lobby');
  const restrictions = usePlayRestrictions({ refetchInterval: RESTRICTION_POLL_MS });
  const { isLocked } = useGamePromoLock();

  if (!game || !refusalIsCertain(game, restrictions) || !isLocked(game)) return null;

  return (
    <div
      role="status"
      className="flex shrink-0 items-start gap-2.5 border-b border-[var(--border)] bg-[var(--warning-bg)] px-3 py-2.5"
    >
      <Icon name="lock" size={16} className="mt-0.5 shrink-0 text-[var(--warning)]" />
      <div className="min-w-0">
        <div className="text-[13px] font-bold text-[var(--text-primary)]">
          {t('promoLocked.frameTitle')}
        </div>
        <p className="mt-0.5 text-[12px] leading-snug text-[var(--text-secondary)]">
          {t('promoLocked.frameBody')}
        </p>
      </div>
    </div>
  );
}
